import { DomainError } from '../errors.js';
import { parseLocalDate } from '../local-date.js';
import {
  cellText,
  controlStatusOf,
  groupsOf,
  RA_FINAL_SHEET_NAMES,
  type RaFinalSheet,
  type RaLibraryCell,
  type RaLibraryIssue,
  type RaLibraryIssueCode,
  scaleOf,
} from './library-import.js';
import { type RaRowControlStatus, type RaVesselGroup, raLinkIssues } from './master.js';

/**
 * Ortağın FINAL risk kütüphanesi teslim paketinin ayrıştırılması (ADR 0098; README 2026-10-20b
 * §3 K1–K5; FINAL 00 READ ME, FINAL 04 Software Workflow, FINAL 05 Developer Schema). Saf adımdır:
 * sayfaları (ilk satır başlık, sütun başlık adıyla) veriye çevirir, satır hatalarını ve uyarılarını
 * toplar. Yazmaz; karşılaştırma ve yazma `@felix/db`'dedir (`importRaFinal`).
 *
 * - Paket onaylı kütüphane değildir ("NOT READY as approved RA templates"): dosyadaki onay,
 *   gözden geçiren ve tarih bilgisi faaliyeti onaylamaz (uyarı `APPROVAL_IGNORED`).
 * - Kimlikler değişmez (FINAL 05 `activity_id`, `risk_candidate_id`): kod doğal anahtardır.
 * - Başlangıç ve kalan olasılık/şiddet bilerek boştur; varsayılan değer verilmez.
 * - Karar kuyruğu (FINAL 03) inceleme işidir; otomatik birleştirme yoktur.
 */

export interface RaFinalSheets {
  readme: RaLibraryCell[][] | null;
  activities: RaLibraryCell[][] | null;
  risks: RaLibraryCell[][] | null;
  queue: RaLibraryCell[][] | null;
}

/** FINAL paketinin sayfa adları (ayrıştırıcının anahtarıyla). */
export const RA_FINAL_SHEETS: Readonly<Record<keyof RaFinalSheets, RaFinalSheet>> = {
  readme: 'final00',
  activities: 'final01',
  risks: 'final02',
  queue: 'final03',
};

/** Çalışma kitabı FINAL biçiminde mi: FINAL 01 ve FINAL 02 sayfaları var. */
export function isRaFinalWorkbook(sheetNames: readonly string[]): boolean {
  const names = new Set(sheetNames.map((n) => n.trim().toLowerCase()));
  return (
    names.has(RA_FINAL_SHEET_NAMES.final01.toLowerCase()) &&
    names.has(RA_FINAL_SHEET_NAMES.final02.toLowerCase())
  );
}

interface Column {
  key: string;
  header: string;
  required: boolean;
}
const col = (key: string, header: string, required = true): Column => ({ key, header, required });

/** Sütun başlıkları (FINAL 05 Developer Schema ile aynı alanlar). */
export const RA_FINAL_COLUMNS: Readonly<Record<keyof RaFinalSheets, readonly Column[]>> = {
  readme: [col('metric', 'Metric / Rule'), col('value', 'Result / Instruction')],
  activities: [
    col('code', 'Activity ID'),
    col('node', 'Parent Node'),
    col('area', 'Area ID'),
    col('areaName', 'Operation Area'),
    col('component', 'System / Component'),
    col('name', 'Selectable Activity'),
    col('applicability', 'Vessel Applicability', false),
    col('pmsReview', 'PMS Review', false),
    col('ptw', 'PTW Screening', false),
    col('candidateCount', 'Risk Candidate Count', false),
    col('queue', 'Technical Review Queue', false),
    col('templateStatus', 'Template Status', false),
    col('vesselApproved', 'Vessel Approved', false),
    col('reviewer', 'Reviewer', false),
    col('reviewDate', 'Review Date', false),
    col('source', 'Original Source', false),
  ],
  risks: [
    col('code', 'Risk Candidate ID'),
    col('activityCode', 'Activity ID'),
    col('area', 'Operation Area', false),
    col('component', 'System / Component', false),
    col('task', 'Task', false),
    col('hazard', 'Hazard / Exposure'),
    col('consequence', 'Potential Consequence', false),
    col('controls', 'Proposed Controls', false),
    col('evidence', 'Verification Evidence', false),
    col('role', 'Responsible Role', false),
    col('when', 'When to Verify', false),
    col('likelihood', 'Initial Likelihood', false),
    col('severity', 'Initial Severity', false),
    col('residualLikelihood', 'Residual Likelihood', false),
    col('residualSeverity', 'Residual Severity', false),
    col('controlStatus', 'Control Verification', false),
    col('technicalStatus', 'Technical Status', false),
    col('reviewer', 'Reviewer', false),
    col('reviewedOn', 'Reviewed On', false),
  ],
  queue: [
    col('issueType', 'Issue Type'),
    col('activityCode', 'Activity ID'),
    col('task', 'Task', false),
    col('reason', 'Reason', false),
    col('related', 'Related Activity IDs', false),
    col('action', 'Required Decision / Action', false),
    col('status', 'Status', false),
    col('reviewer', 'Reviewer', false),
    col('date', 'Date', false),
  ],
};

/** Alan sınırları (ana veri şemasıyla aynı). */
export const RA_FINAL_LIMITS = {
  areaName: 120,
  nodeName: 200,
  activityName: 200,
  text: 300,
  hazard: 300,
  consequence: 200,
  control: 500,
  evidence: 500,
  role: 120,
  when: 500,
  queueText: 1000,
} as const;

export interface RaFinalArea {
  row: number;
  /** Area ID (ör. `RA-05` ya da alt alan `RA-05-01`); kategori kodu olarak aynen kalır. */
  code: string;
  /** Alt alanda "Operation Area / düğüm adı" (ADR 0098 §2). */
  name: string;
}

export interface RaFinalNode {
  /** Parent Node (ör. `RA-01-01`); faaliyet ağacının gruplama düzeyi, seçilemez. */
  code: string;
  name: string;
  areaCode: string;
}

export interface RaFinalActivity {
  row: number;
  code: string;
  areaCode: string;
  nodeCode: string;
  nodeName: string;
  name: string;
  /** Dosyadaki uygunluk metni aynen. */
  applicability: string | null;
  /** Yalnız açık eşleşmede gemi grubu; diğerlerinde ALL + inceleme bayrağı. */
  vesselGroups: RaVesselGroup[];
  applicabilityReview: boolean;
  pmsReview: string | null;
  ptwScreening: string | null;
  reviewQueue: string | null;
  templateStatus: string | null;
  source: string | null;
}

export interface RaFinalCandidate {
  row: number;
  code: string;
  activityCode: string;
  hazard: string;
  consequence: string | null;
  control: string | null;
  evidence: string | null;
  responsibleRole: string | null;
  whenToVerify: string | null;
  likelihood: number | null;
  severity: number | null;
  residualLikelihood: number | null;
  residualSeverity: number | null;
  controlStatus: RaRowControlStatus;
}

export const RA_REVIEW_TASK_TYPES = ['duplicate', 'hazard_review'] as const;
export type RaReviewTaskType = (typeof RA_REVIEW_TASK_TYPES)[number];
export const RA_REVIEW_TASK_STATUSES = ['open', 'closed'] as const;
export type RaReviewTaskStatus = (typeof RA_REVIEW_TASK_STATUSES)[number];

export interface RaFinalQueueItem {
  row: number;
  /** Doğal anahtar: tür ve faaliyet (`DUP-<Activity ID>`, `HAZ-<Activity ID>`). */
  code: string;
  issueType: RaReviewTaskType;
  activityCode: string;
  task: string | null;
  reason: string | null;
  relatedCodes: string[];
  requiredAction: string | null;
  status: RaReviewTaskStatus;
  reviewer: string | null;
  reviewDay: string | null;
}

export interface RaFinalData {
  format: 'final';
  areas: RaFinalArea[];
  nodes: RaFinalNode[];
  activities: RaFinalActivity[];
  candidates: RaFinalCandidate[];
  queue: RaFinalQueueItem[];
  errors: RaLibraryIssue[];
  warnings: RaLibraryIssue[];
}

const CODE = /^[A-Z0-9][A-Z0-9._-]{0,39}$/;

interface RowReader {
  row: number;
  text(key: string): string | null;
}

function rowsOf(sheet: keyof RaFinalSheets, rows: RaLibraryCell[][]): RowReader[] {
  const header = rows[0] ?? [];
  const index = new Map<string, number>();
  header.forEach((cell, i) => {
    const key = (cellText(cell) ?? '').toLowerCase();
    if (key !== '' && !index.has(key)) index.set(key, i);
  });
  const columns = new Map<string, number>();
  for (const column of RA_FINAL_COLUMNS[sheet]) {
    const i = index.get(column.header.toLowerCase());
    if (i === undefined) {
      if (column.required) {
        const detail = `${RA_FINAL_SHEET_NAMES[RA_FINAL_SHEETS[sheet]]}: ${column.header}`;
        throw new DomainError('INVALID_HEADER', detail, detail);
      }
      continue;
    }
    columns.set(column.key, i);
  }
  const readers: RowReader[] = [];
  rows.slice(1).forEach((cells, offset) => {
    if (!cells.some((c) => cellText(c) !== null)) return;
    readers.push({
      row: offset + 2,
      text(key) {
        const i = columns.get(key);
        return i === undefined ? null : cellText(cells[i]);
      },
    });
  });
  return readers;
}

const codeOf = (value: string | null) => (value === null ? null : value.trim().toUpperCase());
const sameText = (a: string | null, b: string | null) =>
  (a ?? '').toLowerCase() === (b ?? '').toLowerCase();

/** Kod düzeyi: `RA-05-01` 2 ayraçlı alt alan; `RA-05` üst alan. */
function isSubArea(code: string): boolean {
  return code.split('-').length > 2;
}

/**
 * Uygunluk metni (ADR 0098 §3): yalnız açık grup adı ("ALL", "CARGO, TANKER") eşlenir; "All /
 * vessel-specific review" gibi metin ALL + inceleme bayrağı olur, metin aynen saklanır.
 */
export function raFinalApplicability(text: string | null): {
  vesselGroups: RaVesselGroup[];
  review: boolean;
  recognized: boolean;
} {
  if (text === null) return { vesselGroups: ['ALL'], review: true, recognized: true };
  const exact = groupsOf(text);
  if (exact) return { vesselGroups: exact, review: false, recognized: true };
  return { vesselGroups: ['ALL'], review: true, recognized: /^all\b/i.test(text) };
}

const PMS_REVIEW_VALUES = ['yes', 'no', 'conditional'];

/** Tarih: `YYYY-MM-DD` ya da Excel seri numarası; boş `null`, geçersiz `undefined`. */
function dayOf(value: string | null): string | null | undefined {
  if (value === null) return null;
  const serial = Number(value);
  if (Number.isFinite(serial) && /^\d+(\.\d+)?$/.test(value)) {
    const ms = Date.UTC(1899, 11, 30) + Math.floor(serial) * 86_400_000;
    return new Date(ms).toISOString().slice(0, 10);
  }
  const day = value.slice(0, 10);
  try {
    parseLocalDate(day);
    return day;
  } catch {
    return undefined;
  }
}

function issueTypeOf(value: string | null): RaReviewTaskType | undefined {
  const v = (value ?? '').toLowerCase().replace(/[\s_-]+/g, ' ');
  if (v === 'duplicate') return 'duplicate';
  if (v === 'hazard review') return 'hazard_review';
  return undefined;
}

function queueStatusOf(value: string | null): RaReviewTaskStatus | undefined {
  const v = (value ?? 'open').toLowerCase();
  if (v === 'open') return 'open';
  if (v === 'closed' || v === 'done' || v === 'resolved') return 'closed';
  return undefined;
}

/** Karar kuyruğu kaydının doğal anahtarı. */
export function raReviewTaskCode(type: RaReviewTaskType, activityCode: string): string {
  return `${type === 'duplicate' ? 'DUP' : 'HAZ'}-${activityCode}`;
}

/** "Approved" görünen ama onay sayılmayan dosya değeri (paket onaylı değildir). */
function looksApproved(value: string | null): boolean {
  if (value === null) return false;
  const v = value.toLowerCase();
  return (v === 'yes' || v.includes('approved')) && !v.includes('not');
}

/**
 * FINAL paketini veriye çevirir (ADR 0098 §1). FINAL 01 ve 02 zorunlu; 03 ve READ ME isteğe
 * bağlı. Gerekli başlık yoksa `INVALID_HEADER`, zorunlu sayfa yoksa `SHEET_MISSING` fırlatır.
 */
export function parseRaFinalSheets(sheets: RaFinalSheets): RaFinalData {
  for (const key of ['activities', 'risks'] as const) {
    if (!sheets[key]) {
      const name = RA_FINAL_SHEET_NAMES[RA_FINAL_SHEETS[key]];
      throw new DomainError('SHEET_MISSING', name, name);
    }
  }
  const read = (key: keyof RaFinalSheets) => {
    const rows = sheets[key];
    return rows ? rowsOf(key, rows) : [];
  };
  const readmeRows = read('readme');
  const activityRows = read('activities');
  const riskRows = read('risks');
  const queueRows = read('queue');

  const errors: RaLibraryIssue[] = [];
  const warnings: RaLibraryIssue[] = [];
  const data: RaFinalData = {
    format: 'final',
    areas: [],
    nodes: [],
    activities: [],
    candidates: [],
    queue: [],
    errors,
    warnings,
  };
  const error = (sheet: RaFinalSheet, row: number, field: string, code: RaLibraryIssueCode) =>
    errors.push({ sheet, row, field, code });
  const warn = (sheet: RaFinalSheet, row: number, field: string, code: RaLibraryIssueCode) =>
    warnings.push({ sheet, row, field, code });

  // FINAL 01 → alanlar, düğümler, faaliyetler
  const areaNames = new Map<string, { row: number; name: string }>();
  const nodes = new Map<string, RaFinalNode>();
  const activities = new Map<string, RaFinalActivity>();
  const candidateCounts = new Map<string, { row: number; count: number }>();
  const variantWarned = new Set<string>();
  for (const r of activityRows) {
    const failed = errors.length;
    const sheet = 'final01';
    const code = codeOf(r.text('code'));
    const node = codeOf(r.text('node'));
    const area = codeOf(r.text('area'));
    for (const [field, value] of [
      ['code', code],
      ['node', node],
      ['area', area],
    ] as const) {
      if (value === null) error(sheet, r.row, field, 'REQUIRED');
      else if (!CODE.test(value)) error(sheet, r.row, field, 'INVALID_CODE');
    }
    if (code !== null && activities.has(code)) error(sheet, r.row, 'code', 'DUPLICATE_CODE');
    // Ağaç: Activity ID düğümün, düğüm alanın altındadır (kimlikten)
    if (code && node && !code.startsWith(`${node}-`))
      error(sheet, r.row, 'node', 'PARENT_MISMATCH');
    if (node && area && !node.startsWith(`${area}-`))
      error(sheet, r.row, 'area', 'PARENT_MISMATCH');
    const areaName = r.text('areaName');
    const component = r.text('component');
    const name = r.text('name');
    for (const [field, value, max] of [
      ['areaName', areaName, RA_FINAL_LIMITS.areaName],
      ['component', component, RA_FINAL_LIMITS.nodeName],
      ['name', name, RA_FINAL_LIMITS.activityName],
    ] as const) {
      if (value === null) error(sheet, r.row, field, 'REQUIRED');
      else if (value.length > max) error(sheet, r.row, field, 'TOO_LONG');
    }
    const texts = {
      applicability: r.text('applicability'),
      pmsReview: r.text('pmsReview'),
      ptw: r.text('ptw'),
      queue: r.text('queue'),
      templateStatus: r.text('templateStatus'),
      source: r.text('source'),
    };
    for (const [field, value] of Object.entries(texts)) {
      if (value !== null && value.length > RA_FINAL_LIMITS.text) {
        error(sheet, r.row, field, 'TOO_LONG');
      }
    }
    if (errors.length > failed || !code || !node || !area || !areaName || !component || !name) {
      continue;
    }
    // Aynı alanın iki yazılışı: ilk yazılış kalır, fark uyarıdır (ör. "Special / Non-Routine")
    const seenArea = areaNames.get(area);
    if (!seenArea) areaNames.set(area, { row: r.row, name: areaName });
    else if (!sameText(seenArea.name, areaName) && !variantWarned.has(`area:${area}`)) {
      variantWarned.add(`area:${area}`);
      warn(sheet, r.row, 'areaName', 'NAME_VARIANT');
    }
    const seenNode = nodes.get(node);
    if (!seenNode) nodes.set(node, { code: node, name: component, areaCode: area });
    else if (seenNode.areaCode !== area) error(sheet, r.row, 'area', 'PARENT_MISMATCH');
    else if (!sameText(seenNode.name, component) && !variantWarned.has(`node:${node}`)) {
      variantWarned.add(`node:${node}`);
      warn(sheet, r.row, 'component', 'NAME_VARIANT');
    }
    const applicability = raFinalApplicability(texts.applicability);
    if (!applicability.recognized) warn(sheet, r.row, 'applicability', 'UNRECOGNIZED_VALUE');
    if (texts.pmsReview !== null && !PMS_REVIEW_VALUES.includes(texts.pmsReview.toLowerCase())) {
      warn(sheet, r.row, 'pmsReview', 'UNRECOGNIZED_VALUE');
    }
    // Paket onay vermez: dosyadaki onay / gözden geçiren / tarih yok sayılır
    if (
      looksApproved(texts.templateStatus) ||
      looksApproved(r.text('vesselApproved')) ||
      r.text('reviewer') !== null ||
      r.text('reviewDate') !== null
    ) {
      warn(sheet, r.row, 'templateStatus', 'APPROVAL_IGNORED');
    }
    const count = r.text('candidateCount');
    if (count !== null) candidateCounts.set(code, { row: r.row, count: Number(count) });
    const activity: RaFinalActivity = {
      row: r.row,
      code,
      areaCode: area,
      nodeCode: node,
      nodeName: component,
      name,
      applicability: texts.applicability,
      vesselGroups: applicability.vesselGroups,
      applicabilityReview: applicability.review,
      pmsReview: texts.pmsReview,
      ptwScreening: texts.ptw,
      reviewQueue: texts.queue,
      templateStatus: texts.templateStatus,
      source: texts.source,
    };
    activities.set(code, activity);
    data.activities.push(activity);
  }
  // Alanlar: alt alanın adı üst alanın adı ve aynı kodlu düğümün adı (RA-05-01 → "Planned
  // Maintenance System / Main Engine"); düğüm yoksa kod
  for (const [code, { row, name }] of areaNames) {
    const full = isSubArea(code) ? `${name} / ${nodes.get(code)?.name ?? code}` : name;
    data.areas.push({ row, code, name: full.slice(0, RA_FINAL_LIMITS.areaName) });
  }
  data.areas.sort((a, b) => a.code.localeCompare(b.code));
  data.nodes = [...nodes.values()].sort((a, b) => a.code.localeCompare(b.code));

  // FINAL 02 → risk / kontrol adayları
  const candidateCodes = new Set<string>();
  const pairs = new Set<string>();
  const perActivity = new Map<string, number>();
  for (const r of riskRows) {
    const sheet = 'final02';
    const failed = errors.length;
    const code = codeOf(r.text('code'));
    if (code === null) error(sheet, r.row, 'code', 'REQUIRED');
    else if (!CODE.test(code)) error(sheet, r.row, 'code', 'INVALID_CODE');
    else if (candidateCodes.has(code)) error(sheet, r.row, 'code', 'DUPLICATE_CODE');
    if (code !== null) candidateCodes.add(code);
    const activityCode = codeOf(r.text('activityCode'));
    if (activityCode === null) error(sheet, r.row, 'activityCode', 'REQUIRED');
    const hazard = r.text('hazard');
    const texts = {
      hazard: [hazard, RA_FINAL_LIMITS.hazard],
      consequence: [r.text('consequence'), RA_FINAL_LIMITS.consequence],
      controls: [r.text('controls'), RA_FINAL_LIMITS.control],
      evidence: [r.text('evidence'), RA_FINAL_LIMITS.evidence],
      role: [r.text('role'), RA_FINAL_LIMITS.role],
      when: [r.text('when'), RA_FINAL_LIMITS.when],
    } as const;
    if (hazard === null) error(sheet, r.row, 'hazard', 'REQUIRED');
    for (const [field, [value, max]] of Object.entries(texts)) {
      if (value !== null && value.length > max) error(sheet, r.row, field, 'TOO_LONG');
    }
    // Olasılık/şiddet: bilerek boş; doluysa 1–5 ve ikisi birlikte. Varsayılan verilmez
    const l = scaleOf(r.text('likelihood'));
    const s = scaleOf(r.text('severity'));
    const rl = scaleOf(r.text('residualLikelihood'));
    const rs = scaleOf(r.text('residualSeverity'));
    for (const [field, value] of [
      ['likelihood', l],
      ['severity', s],
      ['residualLikelihood', rl],
      ['residualSeverity', rs],
    ] as const) {
      if (value === undefined) error(sheet, r.row, field, 'INVALID_SCALE');
    }
    if (l !== undefined && s !== undefined && (l === null) !== (s === null)) {
      error(sheet, r.row, 'likelihood', 'INITIAL_INCOMPLETE');
    }
    const controlStatus = controlStatusOf(r.text('controlStatus'));
    if (!controlStatus) error(sheet, r.row, 'controlStatus', 'INVALID_CONTROL_STATUS');
    if (
      errors.length === failed &&
      l !== undefined &&
      s !== undefined &&
      rl !== undefined &&
      rs !== undefined &&
      controlStatus
    ) {
      const [issue] = raLinkIssues({
        code: code ?? '',
        activityId: '',
        stepId: null,
        hazardId: '',
        likelihood: l,
        severity: s,
        residualLikelihood: rl,
        residualSeverity: rs,
        existingControlIds: [],
        additionalControlIds: [],
        controlStatus,
        evidence: null,
        exposed: null,
        status: 'draft',
      });
      if (issue) error(sheet, r.row, 'residualLikelihood', issue as RaLibraryIssueCode);
    }
    // Aynı faaliyette aynı tehlike bir kez (ana verinin bağ kuralı)
    if (activityCode !== null && hazard !== null) {
      const pair = `${activityCode}\u0000${hazard.toLowerCase()}`;
      if (pairs.has(pair)) error(sheet, r.row, 'hazard', 'DUPLICATE_LINK');
      pairs.add(pair);
    }
    if (errors.length > failed || !code || !activityCode || !hazard || !controlStatus) continue;
    if (l === undefined || s === undefined || rl === undefined || rs === undefined) continue;
    const activity = activities.get(activityCode);
    if (activity && r.text('task') !== null && !sameText(r.text('task'), activity.name)) {
      warn(sheet, r.row, 'task', 'TEXT_MISMATCH');
    }
    if (r.text('reviewer') !== null || r.text('reviewedOn') !== null) {
      warn(sheet, r.row, 'reviewer', 'APPROVAL_IGNORED');
    }
    perActivity.set(activityCode, (perActivity.get(activityCode) ?? 0) + 1);
    data.candidates.push({
      row: r.row,
      code,
      activityCode,
      hazard,
      consequence: texts.consequence[0],
      control: texts.controls[0],
      evidence: texts.evidence[0],
      responsibleRole: texts.role[0],
      whenToVerify: texts.when[0],
      likelihood: l,
      severity: s,
      residualLikelihood: rl,
      residualSeverity: rs,
      controlStatus,
    });
  }
  // Risk Candidate Count: dosyadaki adaylarla aynı mı (uyarı)
  for (const [code, { row, count }] of candidateCounts) {
    if (!Number.isFinite(count) || count !== (perActivity.get(code) ?? 0)) {
      warn('final01', row, 'candidateCount', 'COUNT_MISMATCH');
    }
  }

  // FINAL 03 → karar kuyruğu (inceleme işi; birleştirme yok)
  const queueCodes = new Set<string>();
  for (const r of queueRows) {
    const sheet = 'final03';
    const failed = errors.length;
    const issueType = issueTypeOf(r.text('issueType'));
    if (!issueType) error(sheet, r.row, 'issueType', 'INVALID_ISSUE_TYPE');
    const activityCode = codeOf(r.text('activityCode'));
    if (activityCode === null) error(sheet, r.row, 'activityCode', 'REQUIRED');
    else if (!CODE.test(activityCode)) error(sheet, r.row, 'activityCode', 'INVALID_CODE');
    const related = (r.text('related') ?? '')
      .split(/[\s,;/]+/)
      .map((p) => p.trim().toUpperCase())
      .filter(Boolean);
    if (related.some((c) => !CODE.test(c))) error(sheet, r.row, 'related', 'INVALID_CODE');
    const status = queueStatusOf(r.text('status'));
    if (!status) error(sheet, r.row, 'status', 'INVALID_STATUS');
    const day = dayOf(r.text('date'));
    if (day === undefined) warn(sheet, r.row, 'date', 'INVALID_DATE');
    for (const field of ['task', 'reason', 'action', 'reviewer'] as const) {
      const value = r.text(field);
      if (value !== null && value.length > RA_FINAL_LIMITS.queueText) {
        error(sheet, r.row, field, 'TOO_LONG');
      }
    }
    const code = issueType && activityCode ? raReviewTaskCode(issueType, activityCode) : null;
    if (code !== null && queueCodes.has(code))
      error(sheet, r.row, 'activityCode', 'DUPLICATE_CODE');
    if (code !== null) queueCodes.add(code);
    if (errors.length > failed || !code || !issueType || !activityCode || !status) continue;
    data.queue.push({
      row: r.row,
      code,
      issueType,
      activityCode,
      task: r.text('task'),
      reason: r.text('reason'),
      relatedCodes: [...new Set(related)],
      requiredAction: r.text('action'),
      status,
      reviewer: r.text('reviewer'),
      reviewDay: day ?? null,
    });
  }

  // READ ME sayıları (uyarı): faaliyet, aday, açık inceleme işi
  for (const r of readmeRows) {
    const metric = (r.text('metric') ?? '').toLowerCase();
    const value = Number(r.text('value'));
    const expected: ReadonlyArray<readonly [string, number]> = [
      ['activity candidates', activityRows.length],
      ['risk / control candidate rows', riskRows.length],
      ['unique open review tasks', queueRows.length],
    ];
    for (const [name, actual] of expected) {
      if (metric === name && Number.isFinite(value) && value !== actual) {
        warn('final00', r.row, name, 'COUNT_MISMATCH');
      }
    }
  }

  const order = Object.keys(RA_FINAL_SHEET_NAMES);
  const byPlace = (a: RaLibraryIssue, b: RaLibraryIssue) =>
    order.indexOf(a.sheet) - order.indexOf(b.sheet) || a.row - b.row;
  errors.sort(byPlace);
  warnings.sort(byPlace);
  return data;
}
